import { useSyncExternalStore } from 'react';
import { readAllowedReports } from './jwt';

/*
 * Single-token auth store. Unified auth: the user signs in ONCE at the central
 * auth endpoint (see AUTH_BASE_URL in dashboard/config) and the issued JWT is
 * reused against every region's data backend. So there's exactly one token at
 * a time — no per-backend map.
 *
 * Storage shape (localStorage key `client-portal-ui:auth:v3`):
 *   { token, clientId, role }
 *
 * `allowedReports` is decoded from the token on read — never persisted, so the
 * stored shape can't drift from what the JWT actually claims.
 *
 * On any 401 (from auth OR a data backend) the token is cleared and the user
 * is bounced to /login.
 *
 * v3 supersedes the v2 per-backend map; the old key is ignored (and cleaned up
 * below) so a returning user just re-authenticates once.
 */

const STORAGE_KEY = 'client-portal-ui:auth:v3';
const LEGACY_KEYS = ['client-portal-ui:auth:v2'];

export type AuthEntry = {
  token: string;
  clientId: string | null;
  role: string | null;
  allowedReports: string[];
};

type StoredEntry = Pick<AuthEntry, 'token' | 'clientId' | 'role'>;

function decorate(stored: StoredEntry): AuthEntry {
  return { ...stored, allowedReports: readAllowedReports(stored.token) };
}

function readStored(): AuthEntry | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const o: unknown = JSON.parse(raw);
    if (!o || typeof o !== 'object') return null;
    const r = o as Record<string, unknown>;
    if (typeof r.token !== 'string') return null;
    return decorate({
      token: r.token,
      clientId: typeof r.clientId === 'string' ? r.clientId : null,
      role: typeof r.role === 'string' ? r.role : null,
    });
  } catch {
    return null;
  }
}

let current: AuthEntry | null = readStored();
const listeners = new Set<() => void>();

function notify() {
  for (const cb of listeners) cb();
}

function persist() {
  if (!current) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ token: current.token, clientId: current.clientId, role: current.role }));
}

export function getAuth(): AuthEntry | null {
  return current;
}

export function setAuth(entry: StoredEntry): void {
  current = decorate(entry);
  persist();
  notify();
}

export function clearAuth(): void {
  if (!current) return;
  current = null;
  persist();
  notify();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function useAuth(): AuthEntry | null {
  return useSyncExternalStore(subscribe, getAuth, getAuth);
}

// One-time cleanup of pre-v3 keys so localStorage doesn't carry dead entries.
if (typeof window !== 'undefined') {
  for (const k of LEGACY_KEYS) localStorage.removeItem(k);

  // Cross-tab sync: a sign-out / sign-in in another tab applies here too.
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY || e.key === null) {
      current = readStored();
      notify();
    }
  });
}
